import React, { useState, useEffect } from 'react';
import { Container, Header, Tab, Button, Icon, Segment, Label, Grid, Modal, Message, Input, Form, TextArea } from 'semantic-ui-react';
import AboutAndUserManual from './AboutAndUserManual';
import DropdownConfigurationManager from './DropdownConfigurationManager';
import { api } from '../api/apiClient';
import { SUPPORTED_TIMEZONES } from '../utils/timezone.constants';
import { setUserTimezone as setUserTimezoneUtil, getUserTimezone, formatDateForDisplay } from '../utils/timezone.utils';
import TimezoneClock from './TimezoneClock';

const CORPORATE_RELEASE_TOAST_TEMPLATES = [
    {
        key: 'custom_template',
        name: 'Custom Template',
        icon: 'bell',
        title: '',
        description: ''
    },
    {
        key: 'maintenance_notice',
        name: 'Maintenance Notice',
        icon: 'wrench',
        title: 'Scheduled Maintenance Notice',
        description: 'tenantBridge will undergo planned maintenance from {start_time} to {end_time} ({timezone}). During this window, access to the application may be unavailable. We appreciate your patience and understanding.'
    },
    {
        key: 'release_update_with_downtime',
        name: 'Release Update (With Downtime)',
        icon: 'clock outline',
        title: 'Application Release Update',
        description: 'We are deploying tenantBridge version {version} as part of our scheduled release cycle. The application may be temporarily unavailable from {start_time} to {end_time} ({timezone}). We regret any inconvenience this may cause.'
    },
    {
        key: 'urgent_maintenance',
        name: 'Urgent Maintenance',
        icon: 'warning sign',
        title: 'Urgent Maintenance Communication',
        description: 'tenantBridge is currently undergoing urgent maintenance to address a critical service issue. Application availability may be impacted during this period. Our team is actively working to restore normal service at the earliest.'
    },
    {
        key: 'normal_release_notes',
        name: 'Normal Release Notes',
        icon: 'newspaper outline',
        title: 'Release Notes: v{version}',
        description: 'A new tenantBridge release is now available. This update includes usability enhancements, bug fixes, and stability improvements. Please review the release notes section for full details.'
    }
];

const Settings = ({ user, roles = [], onClose, onLogout, userManualSection = null, initialTab = null }) => {
    const [activeTab, setActiveTab] = useState(initialTab !== null ? initialTab : (userManualSection ? 1 : 0));
    
    // Preferences state
    const [tableDensity, setTableDensity] = useState(() => {
        return localStorage.getItem('tb_tableDensity') || 'comfortable';
    });
    const [rowsPerPage, setRowsPerPage] = useState(() => {
        return localStorage.getItem('tb_rowsPerPage') || '25';
    });
    const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
        const stored = localStorage.getItem('tb_notificationsEnabled');
        return stored !== null ? stored === 'true' : true;
    });
    const [userTimezone, setUserTimezone] = useState(() => {
        return getUserTimezone();
    });
    const [loadingTimezone, setLoadingTimezone] = useState(false);

    // Delete account state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deletingAccount, setDeletingAccount] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [notificationTitle, setNotificationTitle] = useState('');
    const [notificationDescription, setNotificationDescription] = useState('');
    const [notificationIcon, setNotificationIcon] = useState('bell');
    const [selectedTemplateKey, setSelectedTemplateKey] = useState('custom_template');
    const [notificationStartTime, setNotificationStartTime] = useState('');
    const [notificationEndTime, setNotificationEndTime] = useState('');
    const [submittingNotification, setSubmittingNotification] = useState(false);
    const [stoppingNotificationId, setStoppingNotificationId] = useState(null);
    const [deletingNotificationId, setDeletingNotificationId] = useState(null);
    const [notificationSuccess, setNotificationSuccess] = useState('');
    const [notificationError, setNotificationError] = useState('');
    const [scheduledNotifications, setScheduledNotifications] = useState([]);
    const [loadingScheduledNotifications, setLoadingScheduledNotifications] = useState(false);
    const [maintenanceModeEnabled, setMaintenanceModeEnabled] = useState(false);
    const [maintenanceModeLoading, setMaintenanceModeLoading] = useState(false);
    const [maintenanceModeSaving, setMaintenanceModeSaving] = useState(false);
    const [maintenanceModeMessage, setMaintenanceModeMessage] = useState('');
    const [importCloudEnabled, setImportCloudEnabled] = useState(true);
    const [importCloudLoading, setImportCloudLoading] = useState(false);
    const [importCloudSaving, setImportCloudSaving] = useState(false);
    const [importCloudMessage, setImportCloudMessage] = useState('');

    const isDevOps = roles.includes('DEVOPS');

    const handleDeleteAccount = async () => {
        if (deleteConfirmText !== 'DELETE') return;
        setDeletingAccount(true);
        setDeleteError(null);
        try {
            await api.user.deleteAccount();
            setShowDeleteModal(false);
            // Log the user out after account deletion
            if (onLogout) {
                onLogout();
            } else {
                window.location.href = '/logout';
            }
        } catch (error) {
            console.error('Error deleting account:', error);
            setDeleteError(error.response?.data?.message || 'Failed to delete account. Please try again.');
        } finally {
            setDeletingAccount(false);
        }
    };

    // Save preferences to localStorage
    useEffect(() => {
        if (tableDensity) localStorage.setItem('tb_tableDensity', tableDensity);
        if (rowsPerPage) localStorage.setItem('tb_rowsPerPage', rowsPerPage);
        window.dispatchEvent(new CustomEvent('tb:prefsChanged', { detail: { tableDensity, rowsPerPage } }));
    }, [tableDensity, rowsPerPage]);

    useEffect(() => {
        localStorage.setItem('tb_notificationsEnabled', notificationsEnabled.toString());
        window.dispatchEvent(new CustomEvent('tb:notificationsEnabledChanged', { detail: { enabled: notificationsEnabled } }));
    }, [notificationsEnabled]);

    // Fetch user timezone from backend on mount
    useEffect(() => {
        const fetchUserTimezone = async () => {
            try {
                const response = await api.user.getTimezone();
                if (response.data.success && response.data.timezone) {
                    const timezone = response.data.timezone;
                    setUserTimezone(timezone);
                    setUserTimezoneUtil(timezone);
                }
            } catch (error) {
                console.error('Error fetching user timezone:', error);
            }
        };
        if (user) {
            fetchUserTimezone();
        }
    }, [user]);

    // Handle timezone change
    const handleTimezoneChange = async (timezone) => {
        setLoadingTimezone(true);
        try {
            await api.user.updateTimezone(timezone);
            setUserTimezone(timezone);
            setUserTimezoneUtil(timezone);
        } catch (error) {
            console.error('Error updating timezone:', error);
        } finally {
            setLoadingTimezone(false);
        }
    };

    const formatScheduledDisplay = (isoString) => {
        return formatDateForDisplay(isoString, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        }, true);
    };

    const fetchScheduledNotifications = async () => {
        if (!isDevOps) return;
        setLoadingScheduledNotifications(true);
        try {
            const response = await api.admin.getReleaseToasts(50);
            setScheduledNotifications(response.data.notifications || []);
        } catch (error) {
            console.error('Error fetching scheduled release toasts:', error);
        } finally {
            setLoadingScheduledNotifications(false);
        }
    };

    useEffect(() => {
        fetchScheduledNotifications();
    }, [isDevOps]);

    useEffect(() => {
        const fetchMaintenanceMode = async () => {
            if (!isDevOps) return;
            setMaintenanceModeLoading(true);
            try {
                const response = await api.system.getMaintenanceMode();
                setMaintenanceModeEnabled(!!response.data?.enabled);
            } catch (error) {
                console.error('Error fetching maintenance mode:', error);
            } finally {
                setMaintenanceModeLoading(false);
            }
        };
        fetchMaintenanceMode();
    }, [isDevOps]);

    useEffect(() => {
        const fetchImportCloudEnabled = async () => {
            if (!isDevOps) return;
            setImportCloudLoading(true);
            try {
                const response = await api.system.getImportCloudEnabled();
                setImportCloudEnabled(!!response.data?.enabled);
            } catch (error) {
                console.error('Error fetching import cloud setting:', error);
            } finally {
                setImportCloudLoading(false);
            }
        };
        fetchImportCloudEnabled();
    }, [isDevOps]);

    useEffect(() => {
        if (!notificationSuccess) return undefined;
        const timer = setTimeout(() => {
            setNotificationSuccess('');
        }, 3000);
        return () => clearTimeout(timer);
    }, [notificationSuccess]);

    useEffect(() => {
        if (!maintenanceModeMessage) return undefined;
        const timer = setTimeout(() => {
            setMaintenanceModeMessage('');
        }, 3000);
        return () => clearTimeout(timer);
    }, [maintenanceModeMessage]);

    useEffect(() => {
        if (!importCloudMessage) return undefined;
        const timer = setTimeout(() => {
            setImportCloudMessage('');
        }, 3000);
        return () => clearTimeout(timer);
    }, [importCloudMessage]);

    const handleSubmitNotification = async () => {
        setNotificationSuccess('');
        setNotificationError('');

        if (!notificationTitle.trim() || !notificationDescription.trim() || !notificationStartTime || !notificationEndTime) {
            setNotificationError('Title, description, start time, and end time are required.');
            return;
        }

        const startDate = new Date(notificationStartTime);
        const endDate = new Date(notificationEndTime);
        if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
            setNotificationError('Please provide valid start and end time.');
            return;
        }
        if (endDate <= startDate) {
            setNotificationError('End time must be greater than start time.');
            return;
        }
        const startIso = startDate.toISOString();
        const endIso = endDate.toISOString();

        setSubmittingNotification(true);
        try {
            await api.admin.createReleaseToast(
                notificationTitle.trim(),
                notificationDescription.trim(),
                startIso,
                endIso,
                notificationIcon || 'bell'
            );
            setNotificationSuccess('Release toast scheduled successfully.');
            setNotificationTitle('');
            setNotificationDescription('');
            setNotificationIcon('bell');
            setNotificationStartTime('');
            setNotificationEndTime('');
            fetchScheduledNotifications();
        } catch (error) {
            console.error('Error creating release toast:', error);
            setNotificationError(error.response?.data?.message || 'Failed to schedule release toast.');
        } finally {
            setSubmittingNotification(false);
        }
    };

    const handleStopNotification = async (id) => {
        setNotificationSuccess('');
        setNotificationError('');
        setStoppingNotificationId(id);
        try {
            await api.admin.stopReleaseToast(id);
            setNotificationSuccess('Release toast stopped successfully.');
            fetchScheduledNotifications();
        } catch (error) {
            console.error('Error stopping release toast:', error);
            setNotificationError(error.response?.data?.message || 'Failed to stop release toast.');
        } finally {
            setStoppingNotificationId(null);
        }
    };

    const handleDeleteNotification = async (id) => {
        setNotificationSuccess('');
        setNotificationError('');
        setDeletingNotificationId(id);
        try {
            await api.admin.deleteReleaseToast(id);
            setNotificationSuccess('Release toast deleted successfully.');
            fetchScheduledNotifications();
        } catch (error) {
            console.error('Error deleting release toast:', error);
            setNotificationError(error.response?.data?.message || 'Failed to delete release toast.');
        } finally {
            setDeletingNotificationId(null);
        }
    };

    const applyTemplate = (template) => {
        setSelectedTemplateKey(template.key);
        setNotificationTitle(template.title);
        setNotificationDescription(template.description);
        setNotificationIcon(template.icon || 'bell');
        setNotificationSuccess('');
        setNotificationError('');
    };

    const handleMaintenanceModeToggle = async (enabled) => {
        setMaintenanceModeSaving(true);
        setMaintenanceModeMessage('');
        try {
            const response = await api.admin.setMaintenanceMode(enabled);
            const isEnabled = !!response.data?.enabled;
            setMaintenanceModeEnabled(isEnabled);
            setMaintenanceModeMessage(isEnabled
                ? 'Maintenance mode is now enabled.'
                : 'Maintenance mode is now disabled.');
        } catch (error) {
            console.error('Error updating maintenance mode:', error);
            setMaintenanceModeMessage(error.response?.data?.message || 'Failed to update maintenance mode.');
        } finally {
            setMaintenanceModeSaving(false);
        }
    };

    const handleImportCloudToggle = async (enabled) => {
        setImportCloudSaving(true);
        setImportCloudMessage('');
        try {
            const response = await api.admin.setImportCloudEnabled(enabled);
            const isEnabled = !!response.data?.enabled;
            setImportCloudEnabled(isEnabled);
            window.dispatchEvent(new CustomEvent('tb:importCloudEnabledChanged', { detail: { enabled: isEnabled } }));
            setImportCloudMessage(isEnabled
                ? 'Import Cloud is now enabled.'
                : 'Import Cloud is now disabled.');
        } catch (error) {
            console.error('Error updating import cloud setting:', error);
            setImportCloudMessage(error.response?.data?.message || 'Failed to update import cloud setting.');
        } finally {
            setImportCloudSaving(false);
        }
    };


    const getRoleColor = (role) => {
        switch (role) {
            case 'ST_FORM_FILLER':
                return '#35B7F4';
            case 'MT_FORM_FILLER':
                return '#4F46E5';
            case 'MT_DECOMMISSION_REQUESTER':
                return '#0EA5E9';
            case 'MT_DECOMMISSION_APPROVER':
                return '#14B8A6';
            case 'DEVOPS':
                return '#475569';
            case 'APPROVER':
                return '#6F9911';
            case 'LICENSE_GENERATOR':
                return '#F97316';
            case 'CAPACITY_READER':
                return '#9CA3AF';
            default:
                return '#6B7280';
        }
    };

    const panes = [
        {
            menuItem: { key: 'preferences', icon: 'adjust', content: 'Preferences' },
            render: () => (
                <Tab.Pane>
                    <div style={{ padding: '2rem' }}>
                        <Header as="h2" style={{ marginBottom: '2rem', color: '#35B7F4' }}>
                            <Icon name="adjust" />
                            Preferences
                        </Header>

                        <Grid>
                            <Grid.Row>
                                <Grid.Column width={16}>
                                    <Segment>
                                        <Header as="h4" style={{ marginBottom: '1rem' }}>
                                            Table Density
                                        </Header>
                                        <p style={{ color: '#666', marginBottom: '1rem' }}>
                                            Choose how compact you want the table rows to appear.
                                        </p>
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <Button
                                                primary={tableDensity === 'comfortable'}
                                                basic={tableDensity !== 'comfortable'}
                                                onClick={() => setTableDensity('comfortable')}
                                            >
                                                Comfortable
                                            </Button>
                                            <Button
                                                primary={tableDensity === 'compact'}
                                                basic={tableDensity !== 'compact'}
                                                onClick={() => setTableDensity('compact')}
                                            >
                                                Compact
                                            </Button>
                                        </div>
                                    </Segment>
                                </Grid.Column>
                            </Grid.Row>

                            <Grid.Row>
                                <Grid.Column width={16}>
                                    <Segment>
                                        <Header as="h4" style={{ marginBottom: '1rem' }}>
                                            Rows Per Page
                                        </Header>
                                        <p style={{ color: '#666', marginBottom: '1rem' }}>
                                            Select how many rows to display per page in tables.
                                        </p>
                                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                            {['25', '50', '75', '100'].map((count) => (
                                                <Button
                                                    key={count}
                                                    primary={rowsPerPage === count}
                                                    basic={rowsPerPage !== count}
                                                    onClick={() => setRowsPerPage(count)}
                                                >
                                                    {count}
                                                </Button>
                                            ))}
                                        </div>
                                    </Segment>
                                </Grid.Column>
                            </Grid.Row>

                            <Grid.Row>
                                <Grid.Column width={16}>
                                    <Segment>
                                        <Header as="h4" style={{ marginBottom: '1rem' }}>
                                            Notifications
                                        </Header>
                                        <p style={{ color: '#666', marginBottom: '1rem' }}>
                                            Enable or disable in-app notifications.
                                        </p>
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <Button
                                                primary={notificationsEnabled}
                                                basic={!notificationsEnabled}
                                                onClick={() => setNotificationsEnabled(true)}
                                            >
                                                On
                                            </Button>
                                            <Button
                                                primary={!notificationsEnabled}
                                                basic={notificationsEnabled}
                                                onClick={() => setNotificationsEnabled(false)}
                                            >
                                                Off
                                            </Button>
                                        </div>
                                    </Segment>
                                </Grid.Column>
                            </Grid.Row>

                            <Grid.Row>
                                <Grid.Column width={16}>
                                    <Segment>
                                        <Header as="h4" style={{ marginBottom: '1rem' }}>
                                            Timezone
                                        </Header>
                                        <p style={{ color: '#666', marginBottom: '1rem' }}>
                                            Select your preferred timezone for displaying dates and times.
                                        </p>
                                        <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                                            <TimezoneClock timezone={userTimezone} />
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                            {SUPPORTED_TIMEZONES.map((tz) => (
                                                <Button
                                                    key={tz.iana}
                                                    primary={userTimezone === tz.iana}
                                                    basic={userTimezone !== tz.iana}
                                                    onClick={() => handleTimezoneChange(tz.iana)}
                                                    disabled={loadingTimezone}
                                                    loading={loadingTimezone && userTimezone === tz.iana}
                                                >
                                                    {tz.label}
                                                </Button>
                                            ))}
                                        </div>
                                    </Segment>
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    </div>
                </Tab.Pane>
            )
        },
        {
            menuItem: { key: 'user-manual', icon: 'book', content: 'User Manual' },
            render: () => (
                <Tab.Pane style={{ padding: 0 }}>
                    <AboutAndUserManual onClose={null} initialSection={userManualSection || 'about'} />
                </Tab.Pane>
            )
        },
        {
            menuItem: { key: 'account', icon: 'user', content: 'Account' },
            render: () => (
                <Tab.Pane>
                    <div style={{ padding: '2rem' }}>
                        <Header as="h2" style={{ marginBottom: '2rem', color: '#35B7F4' }}>
                            <Icon name="user" />
                            Account Information
                        </Header>

                        <Grid>
                            <Grid.Row>
                                <Grid.Column width={16}>
                                    <Segment>
                                        <Header as="h4" style={{ marginBottom: '1rem' }}>
                                            User Details
                                        </Header>
                                        <div style={{ marginBottom: '1rem' }}>
                                            <strong>Name:</strong> {user?.name || 'N/A'}
                                        </div>
                                        <div style={{ marginBottom: '1rem' }}>
                                            <strong>Email:</strong> {user?.email || 'N/A'}
                                        </div>
                                        {user?.picture && (
                                            <div style={{ marginTop: '1rem' }}>
                                                <img
                                                    src={user.picture}
                                                    alt="Profile"
                                                    style={{
                                                        width: '80px',
                                                        height: '80px',
                                                        borderRadius: '50%',
                                                        objectFit: 'cover'
                                                    }}
                                                    referrerPolicy="no-referrer"
                                                />
                                            </div>
                                        )}
                                    </Segment>
                                </Grid.Column>
                            </Grid.Row>

                            <Grid.Row>
                                <Grid.Column width={16}>
                                    <Segment>
                                        <Header as="h4" style={{ marginBottom: '1rem' }}>
                                            Roles
                                        </Header>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                            {roles.map((role, index) => (
                                                <Label
                                                    key={index}
                                                    size="large"
                                                    style={{
                                                        backgroundColor: getRoleColor(role),
                                                        color: '#FFFFFF',
                                                        borderColor: getRoleColor(role),
                                                        fontSize: '0.9rem',
                                                        padding: '0.6em 1em'
                                                    }}
                                                >
                                                    {role}
                                                </Label>
                                            ))}
                                        </div>
                                    </Segment>
                                </Grid.Column>
                            </Grid.Row>

                            <Grid.Row>
                                <Grid.Column width={16}>
                                    <Segment>
                                        <Header as="h4" style={{ marginBottom: '1rem' }}>
                                            Version Information
                                        </Header>
                                        <div style={{ color: '#666' }}>
                                            <div style={{ marginBottom: '0.5rem' }}>
                                                <strong>Version:</strong> v3.1.1
                                            </div>
                                            <div>
                                                <strong>Released:</strong> June 01, 2026
                                            </div>
                                        </div>
                                    </Segment>
                                </Grid.Column>
                            </Grid.Row>

                            <Grid.Row>
                                <Grid.Column width={16}>
                                    <Segment style={{ border: '1px solid #fecaca', background: '#fef2f2' }}>
                                        <Header as="h4" style={{ marginBottom: '0.5rem', color: '#DC2626' }}>
                                            <Icon name="warning sign" />
                                            Danger Zone
                                        </Header>
                                        <p style={{ color: '#7f1d1d', marginBottom: '1rem', fontSize: '0.95rem' }}>
                                            Permanently delete your account. This action cannot be undone. All your data will be removed from the system and you will lose access immediately.
                                        </p>
                                        <Button
                                            color="red"
                                            onClick={() => {
                                                setShowDeleteModal(true);
                                                setDeleteConfirmText('');
                                                setDeleteError(null);
                                            }}
                                            style={{ fontWeight: '600' }}
                                        >
                                            <Icon name="trash alternate" />
                                            Delete My Account
                                        </Button>
                                    </Segment>
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    </div>
                </Tab.Pane>
            )
        },
        ...(isDevOps ? [{
            menuItem: { key: 'dropdown-configuration', icon: 'list alternate outline', content: 'Dropdown Configuration' },
            render: () => (
                <Tab.Pane>
                    <DropdownConfigurationManager />
                </Tab.Pane>
            )
        }, {
            menuItem: { key: 'control-panel', icon: 'sliders horizontal', content: 'Control Panel' },
            render: () => (
                <Tab.Pane>
                    <div style={{ padding: '2rem' }}>
                        <Header as="h2" style={{ marginBottom: '1.5rem', color: '#35B7F4' }}>
                            <Icon name="sliders horizontal" />
                            Control Panel
                        </Header>

                        <Segment
                            loading={maintenanceModeLoading}
                            style={{
                                borderRadius: '14px',
                                border: '1px solid #e5e7eb',
                                boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05)',
                                padding: '1.2rem'
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '1rem',
                                    flexWrap: 'wrap',
                                    marginBottom: '0.8rem'
                                }}
                            >
                                <Header as="h3" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Icon name="wrench" color={maintenanceModeEnabled ? 'red' : 'green'} />
                                    Maintenance Mode
                                </Header>
                                <Label
                                    style={{
                                        margin: 0,
                                        padding: '0.55em 0.9em',
                                        fontWeight: 700,
                                        letterSpacing: '0.02em',
                                        background: maintenanceModeEnabled ? '#fee2e2' : '#dcfce7',
                                        color: maintenanceModeEnabled ? '#991b1b' : '#166534',
                                        border: maintenanceModeEnabled ? '1px solid #fecaca' : '1px solid #bbf7d0'
                                    }}
                                >
                                    {maintenanceModeEnabled ? 'ENABLED' : 'DISABLED'}
                                </Label>
                            </div>

                            <p style={{ color: '#4b5563', marginBottom: '1.1rem', lineHeight: 1.6 }}>
                                Toggle maintenance mode to control application availability during deployments and planned downtime.
                            </p>

                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: '0.75rem'
                                }}
                            >
                                <Button
                                    size="large"
                                    loading={maintenanceModeSaving}
                                    disabled={maintenanceModeSaving || maintenanceModeEnabled}
                                    onClick={() => handleMaintenanceModeToggle(true)}
                                    style={{
                                        borderRadius: '10px',
                                        fontWeight: 700,
                                        background: maintenanceModeEnabled ? '#f8fafc' : '#ef4444',
                                        color: maintenanceModeEnabled ? '#94a3b8' : '#ffffff',
                                        border: maintenanceModeEnabled ? '1px solid #e2e8f0' : '1px solid #dc2626'
                                    }}
                                >
                                    <Icon name="pause circle" />
                                    Enable Maintenance
                                </Button>
                                <Button
                                    size="large"
                                    loading={maintenanceModeSaving}
                                    disabled={maintenanceModeSaving || !maintenanceModeEnabled}
                                    onClick={() => handleMaintenanceModeToggle(false)}
                                    style={{
                                        borderRadius: '10px',
                                        fontWeight: 700,
                                        background: maintenanceModeEnabled ? '#16a34a' : '#f8fafc',
                                        color: maintenanceModeEnabled ? '#ffffff' : '#94a3b8',
                                        border: maintenanceModeEnabled ? '1px solid #15803d' : '1px solid #e2e8f0'
                                    }}
                                >
                                    <Icon name="play circle" />
                                    Disable Maintenance
                                </Button>
                            </div>

                            {maintenanceModeMessage && (
                                <Message
                                    style={{ marginTop: '1rem', borderRadius: '10px' }}
                                    positive={maintenanceModeMessage.toLowerCase().includes('enabled') || maintenanceModeMessage.toLowerCase().includes('disabled')}
                                    negative={maintenanceModeMessage.toLowerCase().includes('failed')}
                                >
                                    {maintenanceModeMessage}
                                </Message>
                            )}
                        </Segment>
                        <Segment
                            loading={importCloudLoading}
                            style={{
                                marginTop: '1rem',
                                borderRadius: '14px',
                                border: '1px solid #e5e7eb',
                                boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05)',
                                padding: '1.2rem'
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '1rem',
                                    flexWrap: 'wrap',
                                    marginBottom: '0.8rem'
                                }}
                            >
                                <Header as="h3" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Icon name="upload" color={importCloudEnabled ? 'green' : 'grey'} />
                                    Expiring Licenses - Import Cloud
                                </Header>
                                <Label
                                    style={{
                                        margin: 0,
                                        padding: '0.55em 0.9em',
                                        fontWeight: 700,
                                        letterSpacing: '0.02em',
                                        background: importCloudEnabled ? '#dcfce7' : '#f3f4f6',
                                        color: importCloudEnabled ? '#166534' : '#4b5563',
                                        border: importCloudEnabled ? '1px solid #bbf7d0' : '1px solid #d1d5db'
                                    }}
                                >
                                    {importCloudEnabled ? 'ENABLED' : 'DISABLED'}
                                </Label>
                            </div>

                            <p style={{ color: '#4b5563', marginBottom: '1.1rem', lineHeight: 1.6 }}>
                                Control visibility of the Import Cloud button in the Expiring Licenses page.
                            </p>

                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: '0.75rem'
                                }}
                            >
                                <Button
                                    size="large"
                                    loading={importCloudSaving}
                                    disabled={importCloudSaving || importCloudEnabled}
                                    onClick={() => handleImportCloudToggle(true)}
                                    style={{
                                        borderRadius: '10px',
                                        fontWeight: 700,
                                        background: importCloudEnabled ? '#f8fafc' : '#16a34a',
                                        color: importCloudEnabled ? '#94a3b8' : '#ffffff',
                                        border: importCloudEnabled ? '1px solid #e2e8f0' : '1px solid #15803d'
                                    }}
                                >
                                    <Icon name="toggle on" />
                                    Enable Import Cloud
                                </Button>
                                <Button
                                    size="large"
                                    loading={importCloudSaving}
                                    disabled={importCloudSaving || !importCloudEnabled}
                                    onClick={() => handleImportCloudToggle(false)}
                                    style={{
                                        borderRadius: '10px',
                                        fontWeight: 700,
                                        background: importCloudEnabled ? '#475569' : '#f8fafc',
                                        color: importCloudEnabled ? '#ffffff' : '#94a3b8',
                                        border: importCloudEnabled ? '1px solid #334155' : '1px solid #e2e8f0'
                                    }}
                                >
                                    <Icon name="toggle off" />
                                    Disable Import Cloud
                                </Button>
                            </div>

                            {importCloudMessage && (
                                <Message
                                    style={{ marginTop: '1rem', borderRadius: '10px' }}
                                    positive={importCloudMessage.toLowerCase().includes('enabled') || importCloudMessage.toLowerCase().includes('disabled')}
                                    negative={importCloudMessage.toLowerCase().includes('failed')}
                                >
                                    {importCloudMessage}
                                </Message>
                            )}
                        </Segment>
                    </div>
                </Tab.Pane>
            )
        }, {
            menuItem: { key: 'notification-center', icon: 'bell', content: 'Notification Center' },
            render: () => (
                <Tab.Pane>
                    <div style={{ padding: '2rem' }}>
                        <Header as="h2" style={{ marginBottom: '1.5rem', color: '#35B7F4' }}>
                            <Icon name="bell" />
                            Release Toast Center
                        </Header>
                        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                            Configure release toasts shown in the app after login.
                        </p>

                        <Segment>
                            <Header as="h4" style={{ marginBottom: '0.75rem' }}>
                                Default Templates
                            </Header>
                            <p style={{ color: '#666', marginBottom: '1rem' }}>
                                Click a template to prefill title and description.
                            </p>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
                                    gap: '0.6rem',
                                    marginBottom: '1.25rem'
                                }}
                            >
                                {CORPORATE_RELEASE_TOAST_TEMPLATES.map((template) => (
                                    <Button
                                        key={template.key}
                                        basic
                                        size="small"
                                        icon
                                        labelPosition="left"
                                        onClick={() => applyTemplate(template)}
                                        style={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-start',
                                            textAlign: 'left',
                                            minHeight: '40px',
                                            borderRadius: '8px',
                                            border: selectedTemplateKey === template.key
                                                ? '2px solid #35B7F4'
                                                : '1px solid #e5e7eb',
                                            background: '#ffffff',
                                            color: '#374151',
                                            fontWeight: 500,
                                            boxShadow: selectedTemplateKey === template.key
                                                ? 'inset 0 0 0 1px rgba(53,183,244,0.2), 0 1px 2px rgba(0,0,0,0.04)'
                                                : '0 1px 2px rgba(0,0,0,0.04)'
                                        }}
                                    >
                                        <Icon name={template.icon} />
                                        {template.name}
                                    </Button>
                                ))}
                            </div>

                            <Form onSubmit={(e) => { e.preventDefault(); handleSubmitNotification(); }}>
                                <Form.Field
                                    control={Input}
                                    label="Title of the message"
                                    placeholder="Enter title"
                                    value={notificationTitle}
                                    onChange={(e) => setNotificationTitle(e.target.value)}
                                    maxLength={255}
                                />
                                <Form.Field
                                    control={TextArea}
                                    label="Description"
                                    placeholder="Enter description"
                                    value={notificationDescription}
                                    onChange={(e) => setNotificationDescription(e.target.value)}
                                    style={{ minHeight: '120px' }}
                                />
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600, color: '#555' }}>
                                        Icon
                                    </label>
                                    <Label basic style={{ padding: '0.55em 0.8em' }}>
                                        <Icon name={notificationIcon || 'bell'} />
                                        {notificationIcon || 'bell'}
                                    </Label>
                                </div>
                                <Form.Field
                                    control={Input}
                                    label="Start time"
                                    type="datetime-local"
                                    value={notificationStartTime}
                                    onChange={(e) => setNotificationStartTime(e.target.value)}
                                />
                                <Form.Field
                                    control={Input}
                                    label="End time"
                                    type="datetime-local"
                                    value={notificationEndTime}
                                    onChange={(e) => setNotificationEndTime(e.target.value)}
                                />
                                <Button
                                    primary
                                    type="submit"
                                    loading={submittingNotification}
                                    disabled={submittingNotification}
                                    style={{ borderRadius: '8px', fontWeight: 600 }}
                                >
                                    Submit
                                </Button>
                            </Form>

                            {notificationSuccess && (
                                <Message positive style={{ marginTop: '1rem' }}>
                                    {notificationSuccess}
                                </Message>
                            )}
                            {notificationError && (
                                <Message negative style={{ marginTop: '1rem' }}>
                                    {notificationError}
                                </Message>
                            )}
                        </Segment>

                        <Segment loading={loadingScheduledNotifications} style={{ borderRadius: '10px' }}>
                            <Header as="h4" style={{ marginBottom: '1rem' }}>
                                Scheduled Toasts
                            </Header>
                            {scheduledNotifications.length === 0 ? (
                                <p style={{ color: '#666' }}>No toasts scheduled yet.</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
                                    {scheduledNotifications.map((item) => (
                                        <div
                                            key={item.id}
                                            style={{
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '10px',
                                                padding: '1rem',
                                                background: '#fafafa',
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start' }}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontWeight: 700, fontSize: '1rem', color: '#111827' }}>{item.title}</div>
                                                    <div style={{ marginTop: '0.35rem', color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                                                        {item.description}
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                    <Label size="small" color={item.isActive ? 'green' : 'grey'}>
                                                        {item.isActive ? 'Active' : 'Stopped'}
                                                    </Label>
                                                </div>
                                            </div>

                                            <div style={{ marginTop: '0.75rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.5rem' }}>
                                                <div style={{ fontSize: '0.9rem', color: '#4b5563' }}>
                                                    <Icon name="play" style={{ marginRight: '0.25rem' }} />
                                                    <strong>Start:</strong> {formatScheduledDisplay(item.startTime)}
                                                </div>
                                                <div style={{ fontSize: '0.9rem', color: '#4b5563' }}>
                                                    <Icon name="stop" style={{ marginRight: '0.25rem' }} />
                                                    <strong>End:</strong> {formatScheduledDisplay(item.endTime)}
                                                </div>
                                            </div>

                                            <div style={{ marginTop: '0.9rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                                                {item.isActive && (
                                                    <Button
                                                        size="small"
                                                        icon="stop circle"
                                                        content="Stop Toast"
                                                        loading={stoppingNotificationId === item.id}
                                                        disabled={stoppingNotificationId === item.id}
                                                        onClick={() => handleStopNotification(item.id)}
                                                        style={{ borderRadius: '8px', fontWeight: 600 }}
                                                    />
                                                )}
                                                <Button
                                                    size="small"
                                                    basic
                                                    icon="trash alternate"
                                                    content="Delete"
                                                    loading={deletingNotificationId === item.id}
                                                    disabled={deletingNotificationId === item.id}
                                                    onClick={() => handleDeleteNotification(item.id)}
                                                    style={{ borderRadius: '8px', fontWeight: 600 }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Segment>
                    </div>
                </Tab.Pane>
            )
        }] : [])
    ];

    return (
        <div style={{
            minHeight: '100vh',
            background: '#f5f7fa',
            padding: '2rem 0'
        }}>
            <Container style={{ maxWidth: '1400px' }}>
                <div style={{
                    marginBottom: '2rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1.5rem',
                    background: '#ffffff',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                    <Header as="h1" style={{ margin: 0, color: '#35B7F4', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <Icon name="cog" />
                        Settings
                    </Header>
                    {onClose && (
                        <Button
                            onClick={onClose}
                            primary
                            size="large"
                            style={{
                                borderRadius: '6px',
                                fontWeight: '600',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                        >
                            <Icon name="arrow left" />
                            Back to Application
                        </Button>
                    )}
                </div>

                <Tab
                    panes={panes}
                    activeIndex={activeTab}
                    onTabChange={(e, { activeIndex }) => setActiveTab(activeIndex)}
                    menu={{ pointing: true, secondary: true }}
                    style={{
                        background: '#ffffff',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        padding: '1rem'
                    }}
                />
            </Container>

            {/* Delete Account Confirmation Modal */}
            <Modal
                open={showDeleteModal}
                onClose={() => !deletingAccount && setShowDeleteModal(false)}
                size="small"
                closeOnDimmerClick={!deletingAccount}
            >
                <Modal.Header style={{ color: '#DC2626', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Icon name="warning sign" />
                    Delete Account
                </Modal.Header>
                <Modal.Content>
                    <Message negative icon>
                        <Icon name="exclamation triangle" />
                        <Message.Content>
                            <Message.Header>This action is irreversible</Message.Header>
                            <p>Your account and all associated data will be permanently removed. You will be logged out immediately.</p>
                        </Message.Content>
                    </Message>

                    <div style={{ marginTop: '1.5rem' }}>
                        <p style={{ marginBottom: '0.5rem', fontWeight: '600' }}>
                            To confirm, type <span style={{ color: '#DC2626', fontFamily: 'monospace', fontSize: '1.1em' }}>DELETE</span> below:
                        </p>
                        <Input
                            fluid
                            placeholder="Type DELETE to confirm"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            disabled={deletingAccount}
                            style={{ fontFamily: 'monospace' }}
                        />
                    </div>

                    {deleteError && (
                        <Message negative style={{ marginTop: '1rem' }}>
                            <Icon name="times circle" />
                            {deleteError}
                        </Message>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        basic
                        onClick={() => setShowDeleteModal(false)}
                        disabled={deletingAccount}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="red"
                        onClick={handleDeleteAccount}
                        disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
                        loading={deletingAccount}
                    >
                        <Icon name="trash alternate" />
                        Permanently Delete Account
                    </Button>
                </Modal.Actions>
            </Modal>
        </div>
    );
};

export default Settings;

